import type { CreateOfferCargoFormSchemaType } from "@/pages/admin-cargo-create/model/validation";
import { fromIso8601Date, toIso8601Date } from "@/shared/lib/datetime";
import { BaseAccordion } from "@/shared/ui/base-accordion";
import { BaseAccordionSummary } from "@/shared/ui/base-accordion-summary";
import { BaseDatePicker } from "@/shared/ui/base-date-picker";
import { BaseFormLabel } from "@/shared/ui/base-form-label";
import { BaseNumberField } from "@/shared/ui/base-number-field";
import { BaseTextField } from "@/shared/ui/base-text-field";
import { BaseToggle } from "@/shared/ui/base-toggle";
import { AccordionDetails, Box, Grid2, Stack } from "@mui/material";
import { type Control, Controller } from "react-hook-form";

export const PaymentForm = ({
  control,
}: {
  control: Control<CreateOfferCargoFormSchemaType>;
}) => {
  const EXTRA_PAYMENT = [
    { value: 1, label: "あり" },
    { value: 0, label: "なし" },
  ];

  return (
    <BaseAccordion>
      <BaseAccordionSummary>基本運賃</BaseAccordionSummary>
      <AccordionDetails>
        <Stack direction="column" spacing={3}>
          <BaseFormLabel label="運賃" isRequired>
            <Controller
              name="shippingFee"
              control={control}
              render={({ field, fieldState }) => (
                <BaseNumberField
                  {...field}
                  size="small"
                  startAdornment="¥"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </BaseFormLabel>

          <Box display="flex" gap={3}>
            <BaseFormLabel label="有料道路利用料の別途支払い" isRequired>
              <Controller
                name="highwayFee"
                control={control}
                render={({ field }) => {
                  return (
                    <BaseToggle
                      {...field}
                      size="small"
                      options={EXTRA_PAYMENT}
                      fullWidth
                    />
                  );
                }}
              />
            </BaseFormLabel>
            <BaseFormLabel
              label="有料道路区間"
              sx={{ width: "100%", maxWidth: { md: "376px" } }}
            >
              <Controller
                name="highwaySection"
                control={control}
                render={({ field, fieldState }) => (
                  <BaseTextField
                    {...field}
                    size="small"
                    fullWidth
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </BaseFormLabel>
          </Box>
          <Grid2 container spacing={3} width="100%">
            <Grid2 size={{ xs: 12, md: 3 }}>
              <BaseFormLabel label="燃料サーチャージ">
                <Controller
                  name="fuelSurcharge"
                  control={control}
                  render={({ field, fieldState }) => (
                    <BaseNumberField
                      {...field}
                      size="small"
                      startAdornment="¥"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </BaseFormLabel>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <BaseFormLabel label="荷役料金">
                <Controller
                  name="cargoHandlingFee"
                  control={control}
                  render={({ field, fieldState }) => (
                    <BaseNumberField
                      {...field}
                      size="small"
                      startAdornment="¥"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </BaseFormLabel>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <BaseFormLabel label="その他料金">
                <Controller
                  name="othersFee"
                  control={control}
                  render={({ field, fieldState }) => (
                    <BaseNumberField
                      {...field}
                      size="small"
                      startAdornment="¥"
                      type="number"
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </BaseFormLabel>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <BaseFormLabel label="調整金額">
                <Controller
                  name="adjustmentFee"
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <BaseNumberField
                        {...field}
                        size="small"
                        startAdornment="¥"
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                      />
                    );
                  }}
                />
              </BaseFormLabel>
            </Grid2>
          </Grid2>
          <BaseFormLabel label="荷主からの入金予定日">
            <Controller
              name="paymentDate"
              control={control}
              render={({ field, fieldState }) => (
                <BaseDatePicker
                  {...field}
                  value={field.value ? fromIso8601Date(field.value) : null}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  onChange={(v) => {
                    field.onChange(v ? toIso8601Date(v) : null);
                  }}
                />
              )}
            />
          </BaseFormLabel>
          <BaseFormLabel
            label="備考(その他料金の内訳など)"
            sx={{ maxWidth: "376px" }}
          >
            <Controller
              name="basicFeeMemo"
              control={control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  multiline
                  size="small"
                  fullWidth
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </BaseFormLabel>
        </Stack>
      </AccordionDetails>
    </BaseAccordion>
  );
};
